import _ from 'lodash'
import cx from 'classnames'
import { inject, observer } from 'mobx-react'
import React, { Component, Fragment } from 'react'
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl'
import { withRouter } from 'react-router'
import IdleTimer from 'react-idle-timer'
import debounce from 'lodash/debounce'
import {
  AbandonExamModal,
  Activity,
  ActivityModal,
  Button,
  CertificateIcon,
  Heading,
  IdleModal,
  IncompleteQuestionModal,
  Link,
  Loader,
  Modal,
  FeedbackModal,
  MoreContentBelowBanner,
  TryAgainIcon,
} from '@/components'
import {
  checkIfScrollable,
  checkIfScrolledToBottom,
} from '@/components/MoreContentBelowBanner/MoreContentBelowBanner'
import TimeTracking, {
  TYPE_EXAM_ACTIVITY,
} from '@/components/TimeTracking/TimeTracking'
import {
  ABANDON_EXAM_STATUS_ABANDONED,
  ABANDON_EXAM_STATUS_TIMEOUT,
} from '@/stores/models/exam'
import ExamTimer from './ExamTimer'
import './Exam.scss'

const ExamMessages = defineMessages({
  activityErrorBody: {
    defaultMessage:
      'There was an error while loading the current question. Try refreshing the page. If the issue persists, please contact your learning administrator.',
    description:
      'Body copy displayed when there is an issue loading an Activity.',
    id: 'ExamMessages.activityErrorBody',
  },
  activityErrorHeader: {
    defaultMessage: 'Oops, something went wrong.',
    description:
      'Header copy displayed when there is an issue loading an Activity.',
    id: 'ExamMessages.activityErrorHeader',
  },
  abandonExamButton: {
    defaultMessage: 'Abandon Exam',
    description: 'Button to abandon exam during activities.',
    id: 'ExamMessages.abandonExamButton',
  },
  backToDashboardLink: {
    defaultMessage: 'Back to Dashboard',
    description: 'Link to Dashboard on exam outro.',
    id: 'ExamMessages.backToDashboardLink',
  },
  backToExamLink: {
    defaultMessage: 'Back to Exam Home',
    description: 'Link to Exam Home on exam outro.',
    id: 'ExamMessages.backToExamLink',
  },
  beginExamButton: {
    defaultMessage: 'Begin Exam',
    description: 'Button to begin Exam.',
    id: 'ExamMessages.beginExamButton',
  },
  completeBody: {
    defaultMessage: 'Placeholder',
    description: 'Body for exam complete - TODO success and fail',
    id: 'ExamMessages.completeBody',
  },
  confirmAnswerButton: {
    defaultMessage: 'Confirm Answer',
    description: 'Button for submitting an answer to an activity in Exam.',
    id: 'ExamMessages.confirmAnswerButton',
  },
  nextQuestionButton: {
    defaultMessage: 'Next',
    description:
      'Button to proceed to the next question after viewing feedback in practice exam.',
    id: 'ExamMessages.nextQuestionButton',
  },
  downloadCertificateButton: {
    defaultMessage: 'Download Certificate',
    description: 'Button to download exam certificate',
    id: 'ExamMessages.downloadCertificateButton',
  },
  enlargeImage: {
    defaultMessage: 'Enlarge Image',
    description: 'Button to zoom in on activity images in Exam.',
    id: 'ExamMessages.enlargeImage',
  },
  examCompleteDashboardButton: {
    defaultMessage: 'Back to Dashboard',
    description: 'Button to exit exam after completing',
    id: 'ExamMessages.examCompleteDashboardButton',
  },
  examHeader: {
    defaultMessage: '{courseTitle} Final Exam',
    description: 'Header for the Exam.',
    id: 'ExamMessages.examHeader',
  },
  examOnlyIntroDefault1: {
    defaultMessage:
      'Before you begin, please check with your instructor to make sure you are authorized to take the {courseTitle}. In order to pass this Exam, you will need to score a minimum of {scoreRequired}% or higher, on at least {examPassCount} attempts, before you complete.',
    description: 'Default exam only intro copy (1) w/ Exam Only set to true',
    id: 'ExamMessages.examOnlyIntroDefault1',
  },
  examOnlyIntroDefault2: {
    defaultMessage: `Once you are complete, you can download a certificate of completion on your dashboard. You can always come back and practice as many times as you'd like in preparation of your {courseTitle}.`,
    description: 'Default exam only intro copy (2) w/ Exam Only set to true',
    id: 'ExamMessages.examOnlyIntroDefault2',
  },
  examOnlyIntroDefault3: {
    defaultMessage: 'Good luck!',
    description: 'Default exam only intro copy (3) w/ Exam Only set to true',
    id: 'ExamMessages.examOnlyIntroDefault3',
  },
  examIntroDefault1: {
    defaultMessage: 'Hi {firstName},',
    description: 'Default exam intro copy (1)',
    id: 'ExamMessages.examIntroDefault1',
  },
  examIntroDefault2: {
    defaultMessage:
      'Before you officially complete your course, your organization has required that you take a post-exam to demonstrate your knowledge and mastery of this content. You will go through a series of questions and receive a score at the end. If you do not pass, we encourage you to reattempt the post-exam until you do so.',
    description: 'Default exam intro copy (2)',
    id: 'ExamMessages.examIntroDefault2',
  },
  examIntroDefault3: {
    defaultMessage: `Upon passing the exam, your organization may require a certificate of completion. If this is the case, you'll be able to download this document after passing the exam.`,
    description: 'Default exam intro copy (3)',
    id: 'ExamMessages.examIntroDefault3',
  },
  examIntroDefault4: {
    defaultMessage: 'Good luck!',
    description: 'Default exam intro copy (4)',
    id: 'ExamMessages.examIntroDefault4',
  },
  examOnlyOutroFailDefault: {
    defaultMessage:
      'You did not pass your {courseTitle}. In order to pass this Exam, you will need to score a minimum of {scoreRequired}% or higher, on at least {examPassCount} attempts, before you complete.',
    description: 'Default exam outro fail copy w/ Exam Only set to true',
    id: 'ExamMessages.examOnlyOutroFailDefault',
  },
  examOnlyOutroPassDefault: {
    defaultMessage: `In order to complete, you will need to successfully pass {examPassCountRemaining} more attempts, with at least {scoreRequired}% or higher. Keep pushing forward. You're almost there!`,
    description: 'Default exam outro pass copy w/ Exam Only set to true',
    id: 'ExamMessages.examOnlyOutroPassDefault',
  },
  examOnlyOutroPassExamDefault: {
    defaultMessage: `You have successfully passed your Exam with {scoreRequired}% or higher on at least {examPassCount} attempts. You can download a certificate of completion below or on your dashboard. Remember, you can always come back and practice as many times as you'd like.`,
    description:
      'Default exam outro pass copy when all requirements have been met w/ Exam Only set to true',
    id: 'ExamMessages.examOnlyOutroPassExamDefault',
  },
  examOnlyOutroPassExamFailDefault: {
    defaultMessage: `You did not pass this attempt of the {courseTitle}. Keep in mind, however, you have already met the requirement of successful pass rates and attempts. You can download your certificate of completion below or on your dashboard. Feel free to retake the {courseTitle} as many times as you'd like and review your results.`,
    description:
      'Default exam outro pass copy when an attempt did not pass, but, all requirements have already been met w/ Exam Only set to true',
    id: 'ExamMessages.examOnlyOutroPassExamFailDefault',
  },
  examOnlyPassedHeading: {
    defaultMessage: `Congrats, you passed an attempt of your {courseTitle}!`,
    description:
      'Heading in body on done screen when exam is passed w/ Exam Only set to true.',
    id: 'ExamMessages.examOnlyPassedHeading',
  },
  examOnlyPassedExamHeading: {
    defaultMessage: `Congrats, you passed your {courseTitle}!`,
    description:
      'Heading in body on done screen when exam is passed and all requirements have been met w/ Exam Only set to true.',
    id: 'ExamMessages.examOnlyPassedExamHeading',
  },
  examOutroFailDefault: {
    defaultMessage:
      'You have not passed your exam. We encourage you to reattempt again.',
    description: 'Default exam outro fail copy',
    id: 'ExamMessages.examOutroFailDefault',
  },
  examOutroFailNoCertificate: {
    defaultMessage:
      'You have not passed your exam. We encourage you to reattempt again.',
    description: 'Default exam outro fail copy when certificates are disabled',
    id: 'ExamMessages.examOutroFailNoCertificate',
  },
  examOutroPassDefault: {
    defaultMessage: `Congratulations, you passed the exam. If your organization has required a certificate of completion you can download it below. Otherwise, you're all done. Nice work!`,
    description: 'Default exam outro pass copy',
    id: 'ExamMessages.examOutroPassDefault',
  },
  examOutroPassNoCertificate: {
    defaultMessage: `Congratulations, you passed the exam. Nice work!`,
    description: 'Default exam outro pass copy when certificates are disabled',
    id: 'ExamMessages.examOutroPassNoCertificate',
  },
  examResultsBack: {
    defaultMessage: 'Back',
    description: 'Back button for the Exam results screen.',
    id: 'ExamMessages.examResultsBack',
  },
  examResultsHeader: {
    defaultMessage: 'Exam Results',
    description: 'Header for the Exam results screen.',
    id: 'ExamMessages.examResultsHeader',
  },
  failedHeading: {
    defaultMessage: 'Try again',
    description: 'Heading in body on done screen when exam is failed.',
    id: 'ExamMessages.failedHeading',
  },
  feedbackLabel: {
    defaultMessage: 'Leave Feedback:',
    description:
      'Label for the feedback thumbs-up, thumbs-down buttons on the course page for Desktop.',
    id: 'ExamMessages.feedbackLabel',
  },
  feedbackModalLabel: {
    defaultMessage: 'Leave Feedback',
    description: 'Feedback Modal label, and button text on mobile.',
    id: 'ExamMessages.feedbackModalLabel',
  },
  feedbackAdditional: {
    defaultMessage: 'Additional Comment (Optional)',
    description:
      'Description displayed for prompting the user to leave additional, optional feedback on the feedback modal.',
    id: 'ExamMessages.feedbackAdditional',
  },
  goToExamResults: {
    defaultMessage: 'Review Exam',
    description: 'Button to see Exam results.',
    id: 'ExamMessages.goToExamResults',
  },
  moreContentBelow: {
    defaultMessage: 'Scroll For More Content',
    description:
      'Banner to show that there is hidden content below the footer on Exam.',
    id: 'ExamMessages.moreContentBelow',
  },
  passedHeading: {
    defaultMessage: `Congrats, you've passed the exam!`,
    description: 'Heading in body on done screen when exam is passed.',
    id: 'ExamMessages.passedHeading',
  },
  doneSubText: {
    defaultMessage: 'Your score',
    description: 'Subtext in body on done screen when exam is done.',
    id: 'ExamMessages.doneSubText',
  },
  reattemptButton: {
    defaultMessage: 'Retake Exam',
    description: 'Button to reattempt exam on outro failed screen',
    id: 'ExamMessages.reattemptButton',
  },
  refreshButton: {
    defaultMessage: 'Refresh Page',
    description: 'Button displayed when there is an issue loading an Activity.',
    id: 'ExamMessages.refreshButton',
  },
  takeSurveyButton: {
    defaultMessage: 'Take survey',
    description: 'Button to take survey',
    id: 'ExamMessages.takeSurveyButton',
  },
})

const initialStateActivity = {
  activityAnswer: null,
  activityModalImage: null,
  isActivityModalOpen: false,
  isScrollable: false,
  isScrolledToBottom: false,
}

const initialStateDone = {
  // Done Screen States
  // - main
  // - results
  doneScreenState: 'main',
}

const initialState = {
  ...initialStateActivity,
  ...initialStateDone,
  isAbandonModalOpen: false,
}

const getIdleTimeThreshold = () => {
  return 5 * 60 * 1000 // 5 minutes
}

@injectIntl
@withRouter
@inject('contentStore', 'sessionStore', 'uiStore', 'xapiStore')
@observer
class Exam extends Component {
  state = { ...initialState }

  get exam() {
    const { contentStore, exam } = this.props
    const { exam: contentStoreExam } = contentStore
    return exam || contentStoreExam
  }

  onTimeChange = (seconds) => {
    this.exam.timeRemaining = seconds
  }

  saveTimeRemaining = () => {
    const {
      isDone,
      isPractice: isPracticeExam,
      examTimeEnabled,
      isStarted,
      timeRemaining,
    } = this.exam || {}
    if (
      !examTimeEnabled ||
      isPracticeExam ||
      !isStarted ||
      isDone ||
      timeRemaining === null
    ) {
      return Promise.resolve()
    }
    return this.exam.setTimeRemaining(timeRemaining)
  }

  handleVisibilityChange = () => {
    this.saveTimeRemaining()
  }

  componentDidMount() {
    const { isStarted: isStartedExam } = this.exam
    if (isStartedExam) {
      this.onNext()
    }
    window.addEventListener('resize', this.debouncedCheckIfScrollable)
    window.addEventListener('beforeunload', this.saveTimeRemaining)
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    setTimeout(this.checkIfScrollable, 0)
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.debouncedCheckIfScrollable)
    window.removeEventListener('beforeunload', this.saveTimeRemaining)
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange,
    )
    this.$content.removeEventListener('scroll', this.onScroll)
    this.saveTimeRemaining()
    this.terminateSession()
  }
  checkIfScrollable = () => {
    const { isScrollable: wasScrollable } = this.state
    const totalHeight = this.$innerContent
      ? this.$innerContent.clientHeight
      : null
    const visibleHeight = this.$content ? this.$content.clientHeight : null
    checkIfScrollable(
      wasScrollable,
      totalHeight,
      visibleHeight,
      (isScrollable) => {
        this.setState({ isScrollable })
      },
    )
  }
  debouncedCheckIfScrollable = debounce(this.checkIfScrollable, 250)
  launchSession = () => {
    const { xapiStore } = this.props
    const {
      attempt: examAttempt,
      isDone: isDoneExam,
      isPractice: isPracticeExam,
      status: examStatus,
    } = this.exam || {}
    const attempt =
      !examStatus || isDoneExam ? this.exam.incrementAttempt() : examAttempt
    if (isPracticeExam) return Promise.resolve()
    return xapiStore.launchSession('exam', { attempt })
  }
  terminateSession = () => {
    const { xapiStore } = this.props
    const { isPractice: isPracticeExam } = this.exam
    if (isPracticeExam) return Promise.resolve()
    xapiStore.terminateSession()
  }
  onChangeActivity = (activityAnswer) => {
    const { isShowingFeedback } = this.exam || {}
    if (isShowingFeedback) return
    this.setState({ activityAnswer })
  }
  onOpenFeedbackModal = (activityId, feedback) => {
    const { uiStore } = this.props
    uiStore.openFeedbackModal(feedback)
  }
  onCloseFeedbackModal = () => {
    const { uiStore } = this.props
    uiStore.closeFeedbackModal()
  }
  onCloseActivityModal = () => {
    this.setState({
      activityModalImage: null,
      isActivityModalOpen: false,
    })
  }
  onCloseIdleModal = () => {
    const { uiStore } = this.props
    uiStore.closeIdleModal()
  }
  onDone = () => {
    const { history } = this.props
    history.push('/dashboard')
  }
  onDownloadCertificate = () => {
    this.exam.downloadCertificate()
  }
  onIdle = () => {
    const { uiStore } = this.props
    uiStore.openIdleModal()
  }
  onConfirmIncomplete = (event) => {
    this.onSubmitActivity(event, true)
  }
  onClickExamResults = (event) => {
    event.preventDefault()
    this.setState({ doneScreenState: 'results' })
  }
  onClickExamResultsBack = (event) => {
    event.preventDefault()
    this.setState({ doneScreenState: 'main' })
  }
  onClickBack = () => {
    const { onClickBack } = this.props
    return onClickBack && onClickBack()
  }
  onNext = () => {
    this.launchSession()
      .then(this.exam.loadActivity)
      .then(this.checkIfScrollable)
  }
  onOpenActivityModal = (e, image) => {
    if (e) {
      e.stopPropagation()
      e.preventDefault()
    }
    this.setState({
      activityModalImage: image,
      isActivityModalOpen: true,
    })
  }
  onReattemptExam = () => {
    this.exam.resetExam()
    this.launchSession()
      .then(this.exam.loadActivity)
      .then(this.checkIfScrollable)
  }
  onClickAbandonExam = () => {
    this.setState({ isAbandonModalOpen: true })
  }
  onCloseAbandonModal = () => {
    this.setState({ isAbandonModalOpen: false })
  }
  onConfirmAbandonExam = () => {
    this.setState({ isAbandonModalOpen: false })
    this.exam.abandonExam(ABANDON_EXAM_STATUS_ABANDONED).then(() => {
      this.terminateSession()
    })
  }
  onExamTimeout = () => {
    this.exam.abandonExam(ABANDON_EXAM_STATUS_TIMEOUT).then(() => {
      this.terminateSession()
    })
  }
  onScroll = debounce(() => {
    const { isScrolledToBottom } = this.state
    const { isLoadingActivity } = this.exam || {}
    if (this.$content && this.$innerContent) {
      const scrollTop = this.$content.scrollTop + this.$content.clientHeight
      if (
        !isScrolledToBottom &&
        !isLoadingActivity &&
        checkIfScrolledToBottom(scrollTop, this.$innerContent.clientHeight)
      ) {
        this.setState({ isScrolledToBottom: true })
      }
    }
  }, 200)
  onSubmitActivity = (event, confirmedIncomplete = false) => {
    const { contentStore, uiStore } = this.props
    const { activityAnswer } = this.state
    const { openIncompleteQuestionModal } = uiStore
    const {
      isDone: wasDoneExam,
      isPassedExam: wasPassedExam,
      isPractice: isPracticeExam,
      isSavingActivity,
    } = this.exam || {}
    if (!activityAnswer || isSavingActivity) return
    if (!confirmedIncomplete) {
      const isValidAnswer = this.exam.validateAnswer(
        activityAnswer,
        openIncompleteQuestionModal,
      )
      if (!isValidAnswer) return
    }
    const answer = activityAnswer
    if (!isPracticeExam) {
      this.setState({ activityAnswer: null })
    }
    this.saveTimeRemaining()
    this.exam.saveActivity(answer).then(() => {
      const { isDone: isDoneExam, isPassedExam } = this.exam
      if (!wasDoneExam && isDoneExam) {
        this.terminateSession()
        if (!isPracticeExam && !wasPassedExam && isPassedExam) {
          contentStore.sendCourseCompletionMessage()
        }
      }
      this.resetScrollPosition()
    })
  }
  onNextFromFeedback = () => {
    this.setState({ activityAnswer: null })
    this.exam.advanceFromFeedback()
    this.resetScrollPosition()
  }
  resetScrollPosition = () => {
    if (this.$content) {
      this.$content.scrollTo({ top: 0, behavior: 'smooth' })
    }
    this.setState({ isScrolledToBottom: false }, () => {
      this.checkIfScrollable()
    })
  }
  setContentRef = (el) => {
    this.$content = el
    if (!this.hasAddedScrollListener) {
      this.$content.addEventListener('scroll', this.onScroll)
      this.hasAddedScrollListener = true
    }
  }
  setInnerContentRef = (el) => {
    this.$innerContent = el
  }
  __renderActivity(
    activity,
    activityAnswer,
    isSavingActivity = false,
    isActivityModalOpen = false,
    activityModalImage = null,
    onChangeActivity = this.onChangeActivity,
    onSubmitActivity = this.onSubmitActivity,
  ) {
    const { uiStore } = this.props
    const { scrollY } = uiStore
    const {
      // hasNextActivity,
      isShowingFeedback,
      isPractice: isPracticeExam,
    } = this.exam || {}
    const isInFeedbackMode = isPracticeExam && isShowingFeedback
    const {
      activityEmbed,
      activityType,
      answerResponse,
      answersCounter,
      correctAnswers,
      // currentHint,
      goodToKnow,
      // hasHint,
      hasNotPassed,
      id: activityId,
      imageAlignment,
      incorrectAnswers,
      isNewActivity,
      question,
      questionImage,
      questionImageDescription,
      questionImageTitle,
      questionVideo,
      questionVideoUrl,
      replaceActivityMathFormulas,
      title: activityTitle,
      validationError: activityValidationError,
    } = activity || {}
    return !isActivityModalOpen ? (
      <Activity
        id={activityId}
        activityEmbed={activityEmbed}
        answerResponse={isInFeedbackMode ? answerResponse || null : null}
        answersCounter={answersCounter}
        checkIfScrollable={this.checkIfScrollable}
        correctAnswers={isInFeedbackMode ? correctAnswers || null : null}
        // currentActivityHint={isInFeedbackMode ? currentHint : []}
        currentActivityId={activityId}
        dangerLabel={activityValidationError}
        // hasHint={isInFeedbackMode ? hasHint : false}
        hasNextActivity={isInFeedbackMode}
        image={questionImage}
        imageAlignment={imageAlignment}
        imageAlt={questionImageDescription || activityTitle}
        imageTitle={questionImageTitle || activityTitle}
        incorrectAnswers={isInFeedbackMode ? incorrectAnswers || null : null}
        goodToKnow={isInFeedbackMode ? goodToKnow : null}
        isExam
        isDanger={isInFeedbackMode ? hasNotPassed : false}
        isNewActivity={isNewActivity}
        isSavingActivity={isSavingActivity}
        onChange={onChangeActivity}
        onOpenActivityModal={this.onOpenActivityModal}
        onSubmit={onSubmitActivity}
        pageRef={this.$content}
        renderImageAfterQuestion={() =>
          this.renderImageAfterQuestion(
            questionImage,
            questionImageDescription || activityTitle,
            questionImageTitle || activityTitle,
          )
        }
        replaceActivityMathFormulas={replaceActivityMathFormulas}
        scrollY={scrollY}
        title={question}
        type={activityType}
        value={activityAnswer}
        video={questionVideo}
        videoUrl={questionVideoUrl}
        // choices activities
        choice={{
          choices: activity.cleanedChoices,
        }}
        // input activities
        input={{
          isMultipleInput: activity.isMultipleInput,
          textBefore: activity.textBeforeInput,
          textAfter: activity.textAfterInput,
        }}
        // matching activity
        matching={{
          definitions: activity.pairs && activity.pairs.definitions,
          labels: activity.pairs && activity.pairs.labels,
        }}
        // sorting activity
        sortStack={{
          items: activity.pairs && activity.pairs.labels,
        }}
        // hotspot activity
        hotspot={activity.hotSpotData}
        // dragPhrase activity
        dragPhrase={{
          content: activity.cleanedContent,
          categories: activity.categories,
          items: activity.draggableItems,
        }}
      />
    ) : (
      <ActivityModal
        image={activityModalImage}
        isOpen={isActivityModalOpen}
        onClose={this.onCloseActivityModal}
      />
    )
  }
  renderActivity() {
    const { activityAnswer, activityModalImage, isActivityModalOpen } =
      this.state
    const { isSavingActivity, activity } = this.exam || {}
    return this.__renderActivity(
      activity,
      activityAnswer,
      isSavingActivity,
      isActivityModalOpen,
      activityModalImage,
    )
  }
  renderActivityBody() {
    const { xapiStore } = this.props
    const { isLaunchingSession } = xapiStore
    const {
      activity,
      activityError,
      isLoadingActivity,
      isSavingActivity,
      isPractice,
    } = this.exam || {}
    if (activityError) {
      return this.renderActivityError()
    }
    if (
      !activity ||
      isLaunchingSession ||
      isLoadingActivity ||
      (!isPractice && isSavingActivity)
    ) {
      return (
        <div className="Exam__loader-wrapper">
          <Loader className="Exam__loader" />
        </div>
      )
    }
    return (
      <Fragment>
        {this.renderActivity()}
        {this.renderActivityTimeTracking()}
      </Fragment>
    )
  }
  renderActivityError() {
    const { intl } = this.props
    const { formatMessage } = intl
    return (
      <div className="Exam__activity-error">
        <Heading className="Exam__activity-error-header">
          {formatMessage(ExamMessages.activityErrorHeader)}
        </Heading>
        <p className="Exam__activity-error-body">
          {formatMessage(ExamMessages.activityErrorBody)}
        </p>
      </div>
    )
  }
  renderActivityButtons() {
    const { intl } = this.props
    const { activityAnswer } = this.state
    const { formatMessage } = intl
    const {
      isLoadingActivity,
      isSavingActivity,
      isAbandoningExam,
      isPractice,
      isShowingFeedback,
    } = this.exam || {}

    if (isPractice && isShowingFeedback) {
      return (
        <div className="Exam__footer__activityButtons">
          <div className="Exam__footer__buttonWrapper">
            <Modal.Button
              className="Exam__button Exam__button--is-next"
              isSuccess
              key="Exam__button--is-next-feedback"
              onClick={this.onNextFromFeedback}
              qa-id="learner-activity-next-button"
            >
              {formatMessage(ExamMessages.nextQuestionButton)}
            </Modal.Button>
          </div>
        </div>
      )
    }

    return (
      <div className="Exam__footer__activityButtons">
        <div className="Exam__footer__buttonWrapper">
          <Modal.Button
            className="Exam__button Exam__button--is-confirm"
            qa-id="learner-activity-confirm-button"
            disabled={!activityAnswer || _.isEmpty(activityAnswer)}
            isLoading={isLoadingActivity || isSavingActivity}
            isSuccess
            key="Exam__button--is-confirm"
            onClick={this.onSubmitActivity}
          >
            {formatMessage(ExamMessages.confirmAnswerButton)}
          </Modal.Button>
        </div>
        {isPractice && (
          <div className="Exam__footer__buttonWrapper">
            <Modal.Button
              className="Exam__button Exam__button--is-abandon"
              isDanger
              disabled={isAbandoningExam || isSavingActivity}
              isLoading={isAbandoningExam}
              onClick={this.onClickAbandonExam}
              qa-id="learner-activity-abandon-button"
            >
              {formatMessage(ExamMessages.abandonExamButton)}
            </Modal.Button>
          </div>
        )}
      </div>
    )
  }
  renderActivityScreen() {
    const { contentStore, xapiStore } = this.props
    const { settings } = contentStore
    const { isLaunchingSession } = xapiStore
    const {
      activity,
      courseTitle,
      isDone,
      examTimeEnabled,
      isPractice: isPracticeExam,
      isStarted,
      numExamActivitiesRemaining,
      numExamActivitiesTotal,
      isLoadingActivity,
      isSavingActivity,
      timeRemaining,
    } = this.exam || {}
    const { examOnly } = settings || {}
    const progress = activity
      ? Math.min(
          Math.round(
            ((numExamActivitiesTotal - numExamActivitiesRemaining) /
              numExamActivitiesTotal) *
              100,
          ),
          100,
        )
      : 0
    return (
      <Fragment>
        <div
          className="Exam__header-progressMeter"
          style={{ width: `${progress}%` }}
        />
        <Modal.Header className="Exam__header Exam__header--is-activity">
          <Heading className="Exam__header-title" is2>
            <div className="Exam__header-title-main">
              {!examOnly ? (
                <FormattedMessage
                  {...ExamMessages.examHeader}
                  values={{
                    courseTitle: (
                      <span className="Exam__header-course-title">
                        {courseTitle}
                      </span>
                    ),
                  }}
                />
              ) : (
                courseTitle
              )}
            </div>
            {activity ? (
              <div className="Exam__header-counter">
                <span>
                  {numExamActivitiesTotal - numExamActivitiesRemaining + 1}
                </span>
                <span>/</span>
                <span>{numExamActivitiesTotal}</span>
              </div>
            ) : null}
          </Heading>
          {examTimeEnabled &&
            !isPracticeExam &&
            isStarted &&
            !isDone &&
            !isLoadingActivity && (
              <ExamTimer
                timeRemaining={timeRemaining}
                onTimeout={this.onExamTimeout}
                onTimeChange={this.onTimeChange}
                isPaused={
                  isLaunchingSession || isLoadingActivity || isSavingActivity
                }
              />
            )}
        </Modal.Header>
        <Modal.Body
          className={cx('Exam__body Exam__body--is-activity', {
            'Exam__body--is-loading':
              !activity || isLaunchingSession || isLoadingActivity,
          })}
        >
          {this.renderActivityBody()}
        </Modal.Body>
      </Fragment>
    )
  }
  renderActivityTimeTracking() {
    const { uiStore } = this.props
    const { isOpenIdleModal, isOpenIdleSessionModal } = uiStore
    const { activity, courseId, isShowingFeedback } = this.exam || {}
    const { id: activityId } = activity
    return (
      <Fragment>
        {!isShowingFeedback && (
          <TimeTracking
            entityId={activityId}
            isIdle={isOpenIdleModal}
            // not actually passing section
            // used to pass courseId
            isExam
            section={{
              courseId,
            }}
            what={TYPE_EXAM_ACTIVITY}
          />
        )}
        <IdleTimer
          onIdle={this.onIdle}
          timeout={getIdleTimeThreshold()}
          startOnLoad
        />
        <IdleModal
          isOpen={isOpenIdleModal && !isOpenIdleSessionModal}
          onBeforeLogout={this.saveTimeRemaining}
          onClose={this.onCloseIdleModal}
          tab="exam"
        />
      </Fragment>
    )
  }
  renderFeedbackMobile() {
    const { intl } = this.props
    const { formatMessage } = intl
    const { activity } = this.exam || {}
    const { id: activityId } = activity

    if (!activityId) return

    return (
      <div className="Course__feedback-buttons Course__feedback-buttons--is-mobile">
        <Button isText onClick={() => this.onOpenFeedbackModal(activityId)}>
          {formatMessage(ExamMessages.feedbackModalLabel)}
        </Button>
      </div>
    )
  }
  renderFeedback() {
    const { intl, uiStore } = this.props
    const { isMobile } = uiStore
    const { formatMessage } = intl
    const { activity } = this.exam || {}
    const { id: activityId } = activity

    if (isMobile) return this.renderFeedbackMobile()

    return (
      <div className="Exam__feedback-buttons-wrapper">
        <Heading className="Exam__feedback-heading" is4>
          {formatMessage(ExamMessages.feedbackLabel)}
        </Heading>
        <div className="Exam__feedback-buttons">
          <Button
            className="Exam__feedback-button"
            isDanger
            onClick={() => this.onOpenFeedbackModal(activityId, 'negative')}
          >
            <i className="Exam__feedback-icon fa fa-thumbs-down" />
          </Button>
          <Button
            className="Exam__feedback-button"
            isSuccess
            onClick={() => this.onOpenFeedbackModal(activityId, 'positive')}
          >
            <i className="Exam__feedback-icon fa fa-thumbs-up" />
          </Button>
        </div>
      </div>
    )
  }
  renderButtons() {
    const { intl } = this.props
    const { formatMessage } = intl
    const { activity, activityError, isDone, isIntro, isShowingFeedback } =
      this.exam || {}

    if (isIntro) return this.renderIntroButtons()
    if (isDone && !isShowingFeedback) return
    if (activity && activityError) {
      return (
        <Modal.Button
          className="Exam__button Exam__button--is-refresh"
          onClick={(event) => {
            event.preventDefault()
            window.location.reload()
          }}
        >
          {formatMessage(ExamMessages.refreshButton)}
        </Modal.Button>
      )
    }
    if (activity)
      return (
        <div>
          {this.renderFeedback()}
          {this.renderActivityButtons()}
        </div>
      )
    return null
  }
  renderContent() {
    const { isDone, isIntro, isShowingFeedback, isStarted } = this.exam || {}
    if (isDone && !isShowingFeedback) return this.renderDoneScreen()
    if (isIntro) return this.renderIntroScreen()
    if (isStarted || isShowingFeedback) return this.renderActivityScreen()
    return null
  }
  renderDefaultIntro() {
    const { contentStore, intl, sessionStore } = this.props
    const { settings } = contentStore
    const { formatMessage } = intl
    const { user } = sessionStore
    const { courseTitle, examPassCount, scoreRequired } = this.exam || {}
    const { examCertificate, examOnly } = settings || {}
    const { firstName } = user
    if (examOnly) {
      return (
        <div>
          <p>
            {formatMessage(ExamMessages.examOnlyIntroDefault1, {
              courseTitle,
              examPassCount,
              scoreRequired,
            })}
          </p>
          <p>
            {formatMessage(ExamMessages.examOnlyIntroDefault2, { courseTitle })}
          </p>
          <p>{formatMessage(ExamMessages.examOnlyIntroDefault3)}</p>
        </div>
      )
    }
    return (
      <div>
        <p>
          {formatMessage(ExamMessages.examIntroDefault1, {
            firstName: firstName || '',
          })}
        </p>
        <p>{formatMessage(ExamMessages.examIntroDefault2)}</p>
        {examCertificate ? (
          <p>{formatMessage(ExamMessages.examIntroDefault3)}</p>
        ) : null}
        <p>{formatMessage(ExamMessages.examIntroDefault4)}</p>
      </div>
    )
  }
  renderDefaultOutro() {
    const { contentStore, intl } = this.props
    const { formatMessage } = intl
    const { settings } = contentStore
    const {
      courseTitle,
      examPassCount,
      isPassedExam,
      numExamPassed,
      passed,
      scoreRequired,
    } = this.exam || {}
    const { examCertificate, examOnly } = settings || {}
    if (examOnly) {
      if (passed) {
        // some exam requirements have not been met
        if (!isPassedExam) {
          return (
            <div className="Exam__done-copy">
              <p>
                {formatMessage(ExamMessages.examOnlyOutroPassDefault, {
                  examPassCountRemaining: examPassCount - numExamPassed,
                  scoreRequired,
                })}
              </p>
            </div>
          )
        }
        // all exam requirements have been met
        return (
          <div className="Exam__done-copy">
            <p>
              {formatMessage(ExamMessages.examOnlyOutroPassExamDefault, {
                examPassCount,
                scoreRequired,
              })}
            </p>
          </div>
        )
      } else if (isPassedExam) {
        // Failed attempt but all Exam requirements have already been met
        return (
          <div className="Exam__done-copy">
            <p>
              {formatMessage(ExamMessages.examOnlyOutroPassExamFailDefault, {
                courseTitle,
              })}
            </p>
          </div>
        )
      }
      // exam attempt did not receive passing score
      return (
        <div className="Exam__done-copy">
          <p>
            {formatMessage(ExamMessages.examOnlyOutroFailDefault, {
              courseTitle,
              examPassCount,
              scoreRequired,
            })}
          </p>
        </div>
      )
    }
    return (
      <div className="Exam__done-copy">
        <p>
          {examCertificate
            ? formatMessage(
                passed
                  ? ExamMessages.examOutroPassDefault
                  : ExamMessages.examOutroFailDefault,
              )
            : formatMessage(
                passed
                  ? ExamMessages.examOutroPassNoCertificate
                  : ExamMessages.examOutroFailNoCertificate,
              )}{' '}
        </p>
      </div>
    )
  }
  renderDefaultOutroHeading() {
    const { contentStore, intl } = this.props
    const { settings } = contentStore
    const { formatMessage } = intl
    const { courseTitle, isPassedExam, passed } = this.exam || {}
    const { examOnly } = settings || {}
    if (examOnly && passed) {
      if (!isPassedExam) {
        // some exam requirements have not been met
        return (
          <Heading>
            {formatMessage(ExamMessages.examOnlyPassedHeading, { courseTitle })}
          </Heading>
        )
      }
      // all exam requirements have been met
      return (
        <Heading>
          {formatMessage(ExamMessages.examOnlyPassedExamHeading, {
            courseTitle,
          })}
        </Heading>
      )
    }
    return (
      <Heading>
        {formatMessage(
          passed ? ExamMessages.passedHeading : ExamMessages.failedHeading,
        )}
      </Heading>
    )
  }
  renderDoneScreenMain() {
    const { contentStore, intl, onClickBack } = this.props
    const { formatMessage } = intl
    const { settings } = contentStore
    const {
      examPassCount,
      isDownloadingCertificate,
      isLoadingActivity: isLoadingExamActivity,
      isPassedExam,
      isPractice: isPracticeExam,
      numExamPassed,
      outroCopy,
      passed,
      results: examResults,
      score,
      surveyUrl: examSurveyUrl,
    } = this.exam || {}
    const { examCertificate, examEndless } = settings || {}
    return (
      <Fragment>
        {this.renderDefaultOutroHeading()}
        <div className="Exam__done-icon">
          {passed ? <CertificateIcon /> : <TryAgainIcon />}
        </div>
        <span className="Exam__done-subText">
          {formatMessage(ExamMessages.doneSubText)}
        </span>
        <span className="Exam__done-score">{`${Math.floor(score)}%`}</span>
        {outroCopy ? (
          <div
            className="Exam__done-copy"
            dangerouslySetInnerHTML={{ __html: outroCopy }}
          />
        ) : (
          this.renderDefaultOutro()
        )}
        {examResults && examResults.length ? (
          <Modal.Button
            className="Exam__button Exam__button--is-done Exam__results-button"
            isSuccess
            onClick={this.onClickExamResults}
          >
            {formatMessage(ExamMessages.goToExamResults)}
          </Modal.Button>
        ) : null}
        {!isPracticeExam && (
          <div className="Exam__done-buttons">
            {isPassedExam && examSurveyUrl ? (
              <Link
                className="Exam__button Exam__button--is-done button"
                href={examSurveyUrl}
                isButton
                target="_blank"
              >
                {formatMessage(ExamMessages.takeSurveyButton)}
              </Link>
            ) : null}
            {isPassedExam && examCertificate ? (
              <Modal.Button
                className="Exam__button Exam__button--is-done"
                isLoading={isDownloadingCertificate}
                onClick={this.onDownloadCertificate}
              >
                {formatMessage(ExamMessages.downloadCertificateButton)}
              </Modal.Button>
            ) : null}
            {!isPassedExam || examEndless ? (
              <Modal.Button
                className="Exam__button Exam__button--is-done"
                isLoading={isLoadingExamActivity}
                onClick={this.onReattemptExam}
              >
                {formatMessage(ExamMessages.reattemptButton)}
              </Modal.Button>
            ) : null}
          </div>
        )}
        {!isPracticeExam && examPassCount > 1 && (
          <div className="Exam__done-pass-count">
            Passed:{' '}
            {numExamPassed > examPassCount ? examPassCount : numExamPassed} of{' '}
            {examPassCount}
          </div>
        )}
        <div className="Exam__done-link">
          {onClickBack ? (
            <Modal.Button
              className="Exam__button Exam__button--is-done"
              onClick={this.onClickBack}
            >
              {formatMessage(ExamMessages.backToExamLink)}
            </Modal.Button>
          ) : (
            <Link to="/dashboard" qa-id="learner-exam-back-to-dashboard-link">
              {formatMessage(ExamMessages.backToDashboardLink)}
            </Link>
          )}
        </div>
      </Fragment>
    )
  }
  renderDoneScreenResultsItem = (item, index) => {
    const { isPractice: isPracticeExam } = this.exam || {}
    const {
      learningObjectives: itemLearningObjectives,
      passed: itemHasPassed,
      question: itemQuestion,
      activity: itemActivity,
    } = item || {}
    return (
      <div className="Exam__results-item" key={`Exam__results-item-${index}`}>
        <Heading className="Exam__results-item-number" is3>
          Question {index + 1}:
        </Heading>
        <div className="Exam__results-item-question">
          <Heading
            className="Exam__results-item-question-title"
            dangerouslySetInnerHTML={{ __html: itemQuestion }}
            is2
          />
          <div className="Exam__results-item-question-icon">
            <span>
              {itemHasPassed ? (
                <i className="fa fa-check" />
              ) : (
                <i className="fa fa-close" />
              )}
            </span>
          </div>
        </div>
        {itemLearningObjectives &&
          itemLearningObjectives.length &&
          !itemHasPassed && (
            <div className="Exam__results-item-learning-objectives">
              {itemLearningObjectives.map((lo, loIndex) => (
                <p
                  className="Exam__results-item-learning-objective"
                  key={`Exam__results-item-learning-objective-${loIndex}`}
                >
                  * {lo}
                </p>
              ))}
            </div>
          )}
        {isPracticeExam && itemActivity && itemActivity.goodToKnow && (
          <div className="Exam__results-block">
            <span className="Exam__results-item-answer-choice">Feedback:</span>
            <p dangerouslySetInnerHTML={{ __html: itemActivity.goodToKnow }} />
          </div>
        )}
        {itemActivity &&
          itemActivity.type === 'multiple_choice' &&
          !itemHasPassed && (
            <div className="Exam__results-block">
              <span className="Exam__results-item-answer-choice">
                Answer Choices:
              </span>
              {itemActivity.choices.map((choice) => {
                return (
                  <span
                    key={`activity-choice-id-${choice.id}`}
                    className="Exam__results-item-answer-choice-content"
                  >
                    {choice.content}
                  </span>
                )
              })}
            </div>
          )}
      </div>
    )
  }
  renderDoneScreenResults() {
    const { intl } = this.props
    const { formatMessage } = intl
    const { results: examResults } = this.exam || {}
    return (
      <Fragment>
        <Heading>{formatMessage(ExamMessages.examResultsHeader)}</Heading>
        <div className="Exam__results">
          {examResults.map(this.renderDoneScreenResultsItem)}
        </div>
        <Modal.Button
          className="Exam__button Exam__button--is-done Modal__button"
          onClick={this.onClickExamResultsBack}
        >
          {formatMessage(ExamMessages.examResultsBack)}
        </Modal.Button>
      </Fragment>
    )
  }
  renderDoneScreenContent() {
    const { doneScreenState } = this.state
    if (doneScreenState === 'results') {
      return this.renderDoneScreenResults()
    }
    return this.renderDoneScreenMain()
  }
  renderDoneScreen() {
    const { onClickBack } = this.props
    return (
      <Fragment>
        <div className="Exam__header-progressMeter" style={{ width: '100%' }} />
        {!onClickBack && (
          <Link to="/dashboard">
            <i className="fa fa-times" />
          </Link>
        )}
        <Modal.Body className="Exam__body Exam__body--is-done">
          <div className="Exam__done">{this.renderDoneScreenContent()}</div>
        </Modal.Body>
      </Fragment>
    )
  }
  renderImageAfterQuestion = (image, imageAlt, imageTitle) => {
    const { intl } = this.props
    const { formatMessage } = intl
    return (
      <div
        className="Activity-colImage"
        style={{
          backgroundImage: `url(${image})`,
        }}
      >
        <img
          className="Activity-image"
          src={image}
          alt={imageAlt}
          title={imageTitle}
        />
        <Button
          aria-label={formatMessage(ExamMessages.enlargeImage)}
          className="Activity-zoomImage"
          onClick={(e) => this.onOpenActivityModal(e, image)}
        >
          <span>{formatMessage(ExamMessages.enlargeImage)}</span>
          <i
            aria-hidden="true"
            className="fa fa-search-plus"
            title={formatMessage(ExamMessages.enlargeImage)}
          />
        </Button>
      </div>
    )
  }
  renderIntroScreen() {
    const { contentStore } = this.props
    const { settings } = contentStore
    const { courseTitle, introCopy } = this.exam || {}
    const { examOnly } = settings || {}
    return (
      <Fragment>
        <Modal.Header className="Exam__header Exam__header--is-intro">
          <Heading>
            {!examOnly ? (
              <FormattedMessage
                {...ExamMessages.examHeader}
                values={{
                  courseTitle: (
                    <span className="Exam__header-course-title">
                      {courseTitle}
                    </span>
                  ),
                }}
              />
            ) : (
              courseTitle
            )}
          </Heading>
        </Modal.Header>
        <Modal.Body className="Exam__body Exam__body--is-intro">
          {introCopy ? (
            <div dangerouslySetInnerHTML={{ __html: introCopy }} />
          ) : (
            this.renderDefaultIntro()
          )}
        </Modal.Body>
      </Fragment>
    )
  }
  renderIntroButtons() {
    const { intl } = this.props
    const { isLoadingActivity } = this.exam
    const { formatMessage } = intl
    return (
      <Modal.Button
        className="Exam__button Exam__button--is-next"
        isLoading={isLoadingActivity}
        key="Exam__button--is-next"
        onClick={this.onNext}
      >
        {formatMessage(ExamMessages.beginExamButton)}
      </Modal.Button>
    )
  }
  renderMain() {
    const { isScrollable, isScrolledToBottom } = this.state
    const { isIntro, isDone, isLoadingActivity, isShowingFeedback } =
      this.exam || {}
    const shouldDisplayFooter =
      isIntro || (!isLoadingActivity && (!isDone || isShowingFeedback))
    return (
      <Fragment>
        <div className="Exam__content" ref={this.setContentRef}>
          <div ref={this.setInnerContentRef}>{this.renderContent()}</div>
        </div>
        {shouldDisplayFooter ? (
          <Modal.Footer className="Exam__footer">
            <MoreContentBelowBanner
              isScrollable={isScrollable}
              isScrolledToBottom={isScrolledToBottom}
              scrollableEl={this.$content}
              visibleHeight={this.$content ? this.$content.clientHeight : 240}
            />
            {this.renderButtons()}
          </Modal.Footer>
        ) : null}
      </Fragment>
    )
  }
  render() {
    const { intl, uiStore } = this.props
    const { activityAnswer, isAbandonModalOpen } = this.state
    const { formatMessage } = intl
    const {
      activity,
      activityError,
      isDone,
      isIntro,
      isAbandoningExam,
      isShowingFeedback,
    } = this.exam || {}
    const {
      closeIncompleteQuestionModal,
      isOpenIncompleteQuestionModal,
      feedbackModalType,
      isMobile,
      isOpenFeedbackModal,
    } = uiStore
    const answersCountTotals =
      activity &&
      activity.getAnswersCountTotals(
        activityAnswer,
        isOpenIncompleteQuestionModal,
      )
    const { numAnswersIncomplete, numAnswersTotal } = answersCountTotals || {}
    const { id: activityId } = activity || {}
    return (
      <Fragment>
        <Modal className="Exam" isOpen overlayClassName="Exam__overlay">
          <div
            className={cx('Exam__main', {
              'Exam__main--is-activity-error': activityError,
              'Exam__main--is-done': isDone,
              'Exam__main--is-feedback': isShowingFeedback,
              'Exam__main--is-intro': isIntro,
            })}
            qa-id="learner-exam-modal"
          >
            {this.renderMain()}
          </div>
        </Modal>
        <IncompleteQuestionModal
          isOpen={isOpenIncompleteQuestionModal}
          numAnswersIncomplete={numAnswersIncomplete}
          numAnswersTotal={numAnswersTotal}
          onClose={closeIncompleteQuestionModal}
          onConfirm={this.onConfirmIncomplete}
        />
        <FeedbackModal
          activityId={activityId}
          additionalFeedbackMessage={formatMessage(
            ExamMessages.feedbackAdditional,
          )}
          isExam
          feedbackType={feedbackModalType}
          isMobile={isMobile}
          isOpen={isOpenFeedbackModal}
          label={formatMessage(ExamMessages.feedbackModalLabel)}
          onClose={this.onCloseFeedbackModal}
        />
        <AbandonExamModal
          isOpen={isAbandonModalOpen}
          isLoading={isAbandoningExam}
          onClose={this.onCloseAbandonModal}
          onConfirm={this.onConfirmAbandonExam}
        />
      </Fragment>
    )
  }
}

export default Exam
